import React from "react";
import { flushSync } from "react-dom";
import { getIn, useFormikContext } from "formik";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Tooltip,
  Paper,
  Stack,
  Typography,
  Switch,
  FormControlLabel,
  FormHelperText,
} from "@mui/material";
import AddBoxIcon from "@mui/icons-material/AddBox";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import { useSnackbar } from "notistack";
import {
  getAccessSystemSettings,
  openAccessSystemTurnstile,
  startAccessSystemCompressor,
  stopAccessSystemCompressor,
} from "app/services/api/accessSystemSettings";
import {
  findWorkspaceDataIndexByUuid,
  defaultTurnstileOpenRelayNumber,
  TURNSTILE_OPEN_RELAY_MAX,
  TURNSTILE_OPEN_RELAY_MIN,
} from "app/pages/customers/dsok/utils/normalizeDsokWorkspaces";
import {
  DEVICE_TYPE_VALUES,
  generateUuidV4,
} from "./dsokWorkspaceFormConstants";
import TurnstileQrCodeModal, {
  buildTurnstileQrConfig,
} from "./TurnstileQrCodeModal";

const TURNSTILE_OPEN_RELAY_VALUES = Array.from(
  { length: TURNSTILE_OPEN_RELAY_MAX - TURNSTILE_OPEN_RELAY_MIN + 1 },
  (_, i) => i + TURNSTILE_OPEN_RELAY_MIN,
);

// Compressor relays share the same physical Waveshare range (1–8) as turnstiles.
const COMPRESSOR_RELAY_VALUES = TURNSTILE_OPEN_RELAY_VALUES;

// Compressor auto-off: DLA turns the compressor off after this many hours of runtime (1–5).
const COMPRESSOR_AUTO_OFF_HOURS_MIN = 1;
const COMPRESSOR_AUTO_OFF_HOURS_MAX = 5;
const COMPRESSOR_AUTO_OFF_HOURS_VALUES = Array.from(
  {
    length: COMPRESSOR_AUTO_OFF_HOURS_MAX - COMPRESSOR_AUTO_OFF_HOURS_MIN + 1,
  },
  (_, i) => i + COMPRESSOR_AUTO_OFF_HOURS_MIN,
);

// Clamp to an integer in [1, 5]; fall back to the minimum when unset/invalid.
const normalizeCompressorAutoOffHours = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || !Number.isInteger(n)) {
    return COMPRESSOR_AUTO_OFF_HOURS_MIN;
  }
  if (n < COMPRESSOR_AUTO_OFF_HOURS_MIN) return COMPRESSOR_AUTO_OFF_HOURS_MIN;
  if (n > COMPRESSOR_AUTO_OFF_HOURS_MAX) return COMPRESSOR_AUTO_OFF_HOURS_MAX;
  return n;
};

export default function DsokWorkspaceDevicesEditor({
  workspaceUuid,
  customerIdOverride,
  activeDeviceUuid,
  setActiveDeviceUuid,
  showDialog,
  hideDialog,
  /** Show the "Kompresor - betonáreň" device type (only for concreteStation customers). */
  allowCompressor = false,
  t,
}) {
  const { values, setFieldValue, errors, touched } = useFormikContext();
  const { enqueueSnackbar } = useSnackbar();
  const [turnstileQrModalOpen, setTurnstileQrModalOpen] = React.useState(false);
  const [turnstileQrLoading, setTurnstileQrLoading] = React.useState(false);
  const [turnstileQrConfig, setTurnstileQrConfig] = React.useState(null);
  const [turnstileQrLabel, setTurnstileQrLabel] = React.useState("");
  const [turnstileOpenLoading, setTurnstileOpenLoading] = React.useState(null);
  const [compressorStartLoading, setCompressorStartLoading] =
    React.useState(false);
  const [compressorStopLoading, setCompressorStopLoading] =
    React.useState(false);

  const wsIndex = findWorkspaceDataIndexByUuid(
    values?.customer?.workspaces?.data,
    workspaceUuid,
  );

  const devices =
    wsIndex >= 0 ? values?.customer?.workspaces?.data?.[wsIndex]?.devices : [];
  const devicesArray = Array.isArray(devices) ? devices : [];

  const emptyDeviceValue = "__empty_device__";

  if (wsIndex < 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {t("dsokCustomerDetail.workspaces.devices.workspaceNotFound", {
            defaultValue: "Pracovisko sa nepodarilo nájsť v dátach.",
          })}
        </Typography>
      </Paper>
    );
  }

  const deviceRowId = (d, i) =>
    d?.uuid != null && String(d.uuid).trim() !== ""
      ? String(d.uuid).trim()
      : `device-tab-${wsIndex}-${i}`;

  const resolvedDeviceUuid = (() => {
    if (activeDeviceUuid != null && String(activeDeviceUuid).trim() !== "") {
      const match = devicesArray.some(
        (d, i) => deviceRowId(d, i) === String(activeDeviceUuid).trim(),
      );
      if (match) return String(activeDeviceUuid).trim();
      // Selection not in Formik list yet (avoid snapping to devices[0] — wrong row / type).
      return emptyDeviceValue;
    }
    return devicesArray[0] != null
      ? deviceRowId(devicesArray[0], 0)
      : emptyDeviceValue;
  })();

  const deviceIndex = devicesArray.findIndex(
    (d, i) => deviceRowId(d, i) === resolvedDeviceUuid,
  );

  const baseDevicePath =
    deviceIndex >= 0
      ? `customer.workspaces.data.${wsIndex}.devices.${deviceIndex}`
      : null;

  // Always read the active device from Formik values (single source of truth).
  // Using devicesArray[deviceIndex] alone can briefly disagree with values after setFieldValue.
  const device =
    baseDevicePath != null ? getIn(values, baseDevicePath) : undefined;

  const handleAddDevice = () => {
    if (deviceIndex < 0 && devicesArray.length === 0) {
      // noop: just to be explicit
    }
    const uuid = generateUuidV4();
    const newDevice = {
      uuid,
      isActive: true,
      name: "",
      ipAddress: "",
      type: "ticket",
    };

    const next = [...devicesArray, newDevice];
    // Apply Formik state before parent activeDeviceUuid so resolvedDeviceUuid + deviceIndex
    // never briefly point at devices[0] while selection is the new UUID.
    flushSync(() => {
      setFieldValue(`customer.workspaces.data.${wsIndex}.devices`, next);
    });
    setActiveDeviceUuid(String(uuid));
  };

  const handleDeleteDevice = (indexToDelete) => {
    showDialog({
      variant: "confirm",
      title: t("dsokCustomerDetail.workspaces.deleteDeviceConfirm.title", {
        defaultValue: "Delete device?",
      }),
      onYes: () => {
        const next = devicesArray.filter((_, i) => i !== indexToDelete);
        setFieldValue(`customer.workspaces.data.${wsIndex}.devices`, next);
        setActiveDeviceUuid(null);
        hideDialog();
      },
      onNo: hideDialog,
    });
  };

  const handleDeviceTypeChange = (indexToUpdate, newType) => {
    const basePath = `customer.workspaces.data.${wsIndex}.devices.${indexToUpdate}`;
    const dev = getIn(values, basePath) ?? {};
    const currentAdditionalData = dev?.additionalData ?? {};

    // Immutable device replace (shallow clone + explicit additionalData branch).
    let next = { ...dev, type: newType };

    if (newType === "accessTicket") {
      next = {
        ...next,
        additionalData: {
          model: currentAdditionalData?.model ?? "none",
        },
      };
    } else if (newType === "turnstile") {
      const legacyNumber =
        typeof currentAdditionalData?.turnstileNumber === "string"
          ? currentAdditionalData.turnstileNumber.trim()
          : "";
      next = {
        ...next,
        additionalData: {
          turnstileType: currentAdditionalData?.turnstileType ?? "turnstile",
          qrCodeIdIn: currentAdditionalData?.qrCodeIdIn ?? legacyNumber,
          qrCodeIdOut: currentAdditionalData?.qrCodeIdOut ?? "",
          qrCodeIpIn: currentAdditionalData?.qrCodeIpIn ?? "",
          qrCodeIpOut: currentAdditionalData?.qrCodeIpOut ?? "",
          openRelayNumberIn: defaultTurnstileOpenRelayNumber(
            currentAdditionalData?.openRelayNumberIn,
          ),
          openRelayNumberOut: defaultTurnstileOpenRelayNumber(
            currentAdditionalData?.openRelayNumberOut,
          ),
          disableRelayNumberIn: defaultTurnstileOpenRelayNumber(
            currentAdditionalData?.disableRelayNumberIn,
          ),
          disableRelayNumberOut: defaultTurnstileOpenRelayNumber(
            currentAdditionalData?.disableRelayNumberOut,
          ),
        },
      };
    } else if (newType === "compressor") {
      next = {
        ...next,
        additionalData: {
          startRelayNumber: defaultTurnstileOpenRelayNumber(
            currentAdditionalData?.startRelayNumber,
          ),
          disableRelayNumber: defaultTurnstileOpenRelayNumber(
            currentAdditionalData?.disableRelayNumber,
          ),
          autoOffEnabled: currentAdditionalData?.autoOffEnabled === true,
          autoOffHours: normalizeCompressorAutoOffHours(
            currentAdditionalData?.autoOffHours,
          ),
        },
      };
    } else {
      const { additionalData: _omit, ...rest } = next;
      next = rest;
    }

    setFieldValue(basePath, next, false);
  };

  if (!baseDevicePath || resolvedDeviceUuid === emptyDeviceValue) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 1,
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          {t("dsokCustomerDetail.workspaces.devices.sectionTitle", {
            defaultValue: "Zariadenia",
          })}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("dsokCustomerDetail.workspaces.devices.placeholder", {
            defaultValue: "Žiadne zariadenia.",
          })}
        </Typography>

        <Tooltip
          title={t("dsokCustomerDetail.workspaces.devices.addTooltip", {
            defaultValue: "Pridať zariadenie",
          })}
        >
          <IconButton
            aria-label={t("dsokCustomerDetail.workspaces.devices.addTooltip", {
              defaultValue: "Pridať zariadenie",
            })}
            onClick={handleAddDevice}
          >
            <AddBoxIcon />
          </IconButton>
        </Tooltip>
      </Paper>
    );
  }

  const deviceErrors =
    errors?.customer?.workspaces?.data?.[wsIndex]?.devices?.[deviceIndex];
  const deviceTouched =
    touched?.customer?.workspaces?.data?.[wsIndex]?.devices?.[deviceIndex];

  const accessTicketModelError = deviceErrors?.additionalData?.model;
  const accessTicketModelTouched = deviceTouched?.additionalData?.model;

  const turnstileTypeError = deviceErrors?.additionalData?.turnstileType;
  const turnstileTypeTouched = deviceTouched?.additionalData?.turnstileType;

  const openRelayInError = deviceErrors?.additionalData?.openRelayNumberIn;
  const openRelayInTouched = deviceTouched?.additionalData?.openRelayNumberIn;
  const openRelayOutError = deviceErrors?.additionalData?.openRelayNumberOut;
  const openRelayOutTouched = deviceTouched?.additionalData?.openRelayNumberOut;

  const disableRelayInError =
    deviceErrors?.additionalData?.disableRelayNumberIn;
  const disableRelayInTouched =
    deviceTouched?.additionalData?.disableRelayNumberIn;
  const disableRelayOutError =
    deviceErrors?.additionalData?.disableRelayNumberOut;
  const disableRelayOutTouched =
    deviceTouched?.additionalData?.disableRelayNumberOut;

  const rawType =
    device && Object.prototype.hasOwnProperty.call(device, "type")
      ? device.type
      : "ticket";
  const type = DEVICE_TYPE_VALUES.includes(rawType) ? rawType : "ticket";
  const additionalData = device?.additionalData ?? {};
  const customerId = customerIdOverride ?? values?.customer?.customerId;
  const currentWorkspaceId = workspaceUuid;
  const currentDeviceId = device?.uuid;
  const qrCodeIdIn = String(additionalData?.qrCodeIdIn ?? "").trim();
  const qrCodeIdOut = String(additionalData?.qrCodeIdOut ?? "").trim();
  const qrCodeIpIn = String(additionalData?.qrCodeIpIn ?? "").trim();
  const qrCodeIpOut = String(additionalData?.qrCodeIpOut ?? "").trim();
  const qrGenerationInDisabled =
    turnstileQrLoading || qrCodeIdIn === "" || qrCodeIpIn === "";
  const qrGenerationOutDisabled =
    turnstileQrLoading || qrCodeIdOut === "" || qrCodeIpOut === "";
  const deviceTypeLabelText = t("dsokCustomerDetail.workspaces.devices.type", {
    defaultValue: "Typ zariadenia",
  });

  const handleOpenTurnstile = async (direction) => {
    if (turnstileOpenLoading) return;

    const payload = {
      customerId,
      workspaceId: currentWorkspaceId,
      deviceId: currentDeviceId,
      direction,
    };

    const hasRequiredPayload =
      payload.customerId != null &&
      String(payload.customerId).trim() !== "" &&
      payload.workspaceId != null &&
      String(payload.workspaceId).trim() !== "" &&
      payload.deviceId != null &&
      String(payload.deviceId).trim() !== "";

    if (!hasRequiredPayload) {
      enqueueSnackbar(
        t(
          "dsokCustomerDetail.workspaces.devices.turnstile.openAction.missingPayload",
          {
            defaultValue: "Chýbajú údaje potrebné na otvorenie turniketu.",
          },
        ),
        { variant: "warning" },
      );
      return;
    }

    setTurnstileOpenLoading(direction);
    try {
      await openAccessSystemTurnstile(payload);
      enqueueSnackbar(
        t(
          "dsokCustomerDetail.workspaces.devices.turnstile.openAction.success",
          {
            defaultValue: "Turniket otvorený",
          },
        ),
        { variant: "success" },
      );
    } catch (e) {
      const backendMessage =
        e?.response?.data?.reason ||
        e?.response?.data?.message ||
        e?.response?.data?.error;
      enqueueSnackbar(
        backendMessage ||
          t(
            "dsokCustomerDetail.workspaces.devices.turnstile.openAction.error",
            {
              defaultValue: "Turniket sa nepodarilo otvoriť.",
            },
          ),
        { variant: "error" },
      );
    } finally {
      setTurnstileOpenLoading(null);
    }
  };

  const handleStartCompressor = async () => {
    if (compressorStartLoading) return;

    const payload = {
      customerId,
      workspaceId: currentWorkspaceId,
      deviceId: currentDeviceId,
    };

    const hasRequiredPayload =
      payload.customerId != null &&
      String(payload.customerId).trim() !== "" &&
      payload.workspaceId != null &&
      String(payload.workspaceId).trim() !== "" &&
      payload.deviceId != null &&
      String(payload.deviceId).trim() !== "";

    if (!hasRequiredPayload) {
      enqueueSnackbar(
        t(
          "dsokCustomerDetail.workspaces.devices.compressor.startAction.missingPayload",
          {
            defaultValue: "Chýbajú údaje potrebné na spustenie kompresora.",
          },
        ),
        { variant: "warning" },
      );
      return;
    }

    setCompressorStartLoading(true);
    try {
      await startAccessSystemCompressor(payload);
      enqueueSnackbar(
        t(
          "dsokCustomerDetail.workspaces.devices.compressor.startAction.success",
          {
            defaultValue: "Kompresor spustený",
          },
        ),
        { variant: "success" },
      );
    } catch (e) {
      const backendMessage =
        e?.response?.data?.reason ||
        e?.response?.data?.message ||
        e?.response?.data?.error;
      enqueueSnackbar(
        backendMessage ||
          t(
            "dsokCustomerDetail.workspaces.devices.compressor.startAction.error",
            {
              defaultValue: "Kompresor sa nepodarilo spustiť.",
            },
          ),
        { variant: "error" },
      );
    } finally {
      setCompressorStartLoading(false);
    }
  };

  const handleStopCompressor = async () => {
    if (compressorStopLoading) return;

    const payload = {
      customerId,
      workspaceId: currentWorkspaceId,
      deviceId: currentDeviceId,
    };

    const hasRequiredPayload =
      payload.customerId != null &&
      String(payload.customerId).trim() !== "" &&
      payload.workspaceId != null &&
      String(payload.workspaceId).trim() !== "" &&
      payload.deviceId != null &&
      String(payload.deviceId).trim() !== "";

    if (!hasRequiredPayload) {
      enqueueSnackbar(
        t(
          "dsokCustomerDetail.workspaces.devices.compressor.stopAction.missingPayload",
          {
            defaultValue: "Chýbajú údaje potrebné na vypnutie kompresora.",
          },
        ),
        { variant: "warning" },
      );
      return;
    }

    setCompressorStopLoading(true);
    try {
      await stopAccessSystemCompressor(payload);
      enqueueSnackbar(
        t(
          "dsokCustomerDetail.workspaces.devices.compressor.stopAction.success",
          {
            defaultValue: "Kompresor vypnutý",
          },
        ),
        { variant: "success" },
      );
    } catch (e) {
      const backendMessage =
        e?.response?.data?.reason ||
        e?.response?.data?.message ||
        e?.response?.data?.error;
      enqueueSnackbar(
        backendMessage ||
          t(
            "dsokCustomerDetail.workspaces.devices.compressor.stopAction.error",
            {
              defaultValue: "Kompresor sa nepodarilo vypnúť.",
            },
          ),
        { variant: "error" },
      );
    } finally {
      setCompressorStopLoading(false);
    }
  };

  const handleOpenTurnstileQrModal = async (direction) => {
    const isOut = direction === "out";
    const qrCodeId = isOut ? qrCodeIdOut : qrCodeIdIn;
    const qrCodeIp = isOut ? qrCodeIpOut : qrCodeIpIn;
    const disabled = isOut ? qrGenerationOutDisabled : qrGenerationInDisabled;

    if (disabled) {
      if (!turnstileQrLoading) {
        enqueueSnackbar(
          t(
            "dsokCustomerDetail.workspaces.devices.turnstile.qrModal.missingIp",
            {
              defaultValue:
                "Vyplňte ID QR code a IP adresu pred generovaním QR kódu.",
            },
          ),
          { variant: "warning" },
        );
      }
      return;
    }

    setTurnstileQrLoading(true);
    try {
      const data = await getAccessSystemSettings();
      const settingsRoot = data?.settings ?? data ?? {};
      const turnstilesSettings = settingsRoot?.turnstilesSettings;
      const hasRequiredSettings =
        turnstilesSettings?.dlaServerIp &&
        turnstilesSettings?.mask &&
        turnstilesSettings?.gateway &&
        turnstilesSettings?.dns;

      if (!hasRequiredSettings) {
        enqueueSnackbar(
          t(
            "dsokCustomerDetail.workspaces.devices.turnstile.qrModal.missingSettings",
            {
              defaultValue:
                "Chýbajú nastavenia turniketov v nastaveniach prístupového systému.",
            },
          ),
          { variant: "error" },
        );
        return;
      }

      setTurnstileQrConfig(
        buildTurnstileQrConfig(turnstilesSettings, qrCodeIp, qrCodeId),
      );
      setTurnstileQrLabel(
        isOut
          ? t("dsokCustomerDetail.workspaces.devices.turnstile.qrModal.out", {
              defaultValue: "QR OUT",
            })
          : t("dsokCustomerDetail.workspaces.devices.turnstile.qrModal.in", {
              defaultValue: "QR IN",
            }),
      );
      setTurnstileQrModalOpen(true);
    } catch (e) {
      enqueueSnackbar(
        t(
          "dsokCustomerDetail.workspaces.devices.turnstile.qrModal.loadFailed",
          {
            defaultValue: "Nepodarilo sa načítať nastavenia pre QR kód.",
          },
        ),
        { variant: "error" },
      );
    } finally {
      setTurnstileQrLoading(false);
    }
  };

  return (
    <Paper
      key={`workspace-devices-${String(workspaceUuid)}-${String(resolvedDeviceUuid)}`}
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 1,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          {t("dsokCustomerDetail.workspaces.devices.sectionTitle", {
            defaultValue: "Zariadenia",
          })}
        </Typography>

        <Tooltip
          title={t("dsokCustomerDetail.workspaces.devices.addTooltip", {
            defaultValue: "Pridať zariadenie",
          })}
        >
          <IconButton
            aria-label={t("dsokCustomerDetail.workspaces.devices.addTooltip", {
              defaultValue: "Pridať zariadenie",
            })}
            onClick={handleAddDevice}
          >
            <AddBoxIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Plain Buttons: ToggleButtonGroup exclusive mode can ignore clicks / not update controlled value reliably. */}
      <Stack direction="row" flexWrap="wrap" spacing={1} sx={{ mb: 2, gap: 1 }}>
        {devicesArray.map((d, i) => {
          const id = deviceRowId(d, i);
          const selected = resolvedDeviceUuid === id;
          return (
            <Button
              key={id}
              type="button"
              size="small"
              variant={selected ? "contained" : "outlined"}
              aria-pressed={selected}
              aria-label={d?.name?.trim() ? d.name : "—"}
              onClick={() => setActiveDeviceUuid(id)}
              sx={{ textTransform: "none" }}
            >
              {d?.name?.trim() ? d.name : "—"}
            </Button>
          );
        })}
      </Stack>

      <Stack spacing={2}>
        {/* Row 1: Aktívne + UUID (+ delete); stacks on narrow viewports */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={2}
          sx={{ rowGap: 2 }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(device?.isActive)}
                onChange={(e) =>
                  setFieldValue(`${baseDevicePath}.isActive`, e.target.checked)
                }
              />
            }
            label={t("dsokCustomerDetail.workspaces.devices.isActive", {
              defaultValue: "Aktívne",
            })}
            sx={{
              flexShrink: 0,
              alignSelf: { xs: "flex-start", sm: "center" },
            }}
          />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <JumboTextField
              fullWidth
              name={`${baseDevicePath}.uuid`}
              label={t("dsokCustomerDetail.workspaces.devices.uuid", {
                defaultValue: "UUID",
              })}
              InputProps={{ readOnly: true }}
            />
          </Box>

          <Button
            variant="outlined"
            color="error"
            onClick={() => handleDeleteDevice(deviceIndex)}
            size="small"
            sx={{
              height: 40,
              flexShrink: 0,
              alignSelf: { xs: "flex-start", sm: "center" },
            }}
          >
            {t("dsokCustomerDetail.workspaces.devices.delete", {
              defaultValue: "Zmazať",
            })}
          </Button>
        </Stack>

        {/* Row 2: Názov + IP adresa */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ rowGap: 2 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <JumboTextField
              fullWidth
              required
              name={`${baseDevicePath}.name`}
              label={t("dsokCustomerDetail.workspaces.devices.name", {
                defaultValue: "Názov",
              })}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <JumboTextField
              fullWidth
              required
              name={`${baseDevicePath}.ipAddress`}
              label={t("dsokCustomerDetail.workspaces.devices.ipAddress", {
                defaultValue: "IP adresa",
              })}
            />
          </Box>
        </Stack>

        <FormControl
          component="fieldset"
          variant="standard"
          sx={{ mt: 1 }}
          error={false}
        >
          <FormLabel component="legend">{deviceTypeLabelText}</FormLabel>
          <RadioGroup
            name={`device-type-${String(workspaceUuid)}-${String(resolvedDeviceUuid)}`}
            value={type}
            onChange={(e) =>
              handleDeviceTypeChange(deviceIndex, e.target.value)
            }
          >
            <FormControlLabel
              value="ticket"
              control={<Radio color="primary" />}
              label={t(
                "dsokCustomerDetail.workspaces.devices.typeOptions.ticket",
                { defaultValue: "ticket" },
              )}
            />
            <FormControlLabel
              value="kitchen"
              control={<Radio color="primary" />}
              label={t(
                "dsokCustomerDetail.workspaces.devices.typeOptions.kitchen",
                { defaultValue: "kitchen" },
              )}
            />
            <FormControlLabel
              value="accessTicket"
              control={<Radio color="primary" />}
              label={t(
                "dsokCustomerDetail.workspaces.devices.typeOptions.accessTicket",
                { defaultValue: "accessTicket" },
              )}
            />
            <FormControlLabel
              value="turnstile"
              control={<Radio color="primary" />}
              label={t(
                "dsokCustomerDetail.workspaces.devices.typeOptions.turnstile",
                { defaultValue: "turnstile" },
              )}
            />
            {(allowCompressor || type === "compressor") && (
              <FormControlLabel
                value="compressor"
                control={<Radio color="primary" />}
                label={t(
                  "dsokCustomerDetail.workspaces.devices.typeOptions.compressor",
                  { defaultValue: "Kompresor - betonáreň" },
                )}
              />
            )}
          </RadioGroup>
        </FormControl>

        {type === "accessTicket" && (
          <FormControl
            component="fieldset"
            variant="standard"
            sx={{ mt: 1 }}
            error={Boolean(accessTicketModelTouched && accessTicketModelError)}
          >
            <FormLabel component="legend">
              {t("dsokCustomerDetail.workspaces.devices.accessTicket.model", {
                defaultValue: "Model",
              })}
            </FormLabel>
            <RadioGroup
              name={`accessTicket-model-${String(resolvedDeviceUuid)}`}
              value={additionalData?.model ?? "none"}
              onChange={(e) => {
                const nextModel = e.target.value;
                setFieldValue(
                  `${baseDevicePath}.additionalData`,
                  {
                    model: nextModel,
                  },
                  false,
                );
              }}
            >
              <FormControlLabel
                value="none"
                control={<Radio color="primary" />}
                label={t(
                  "dsokCustomerDetail.workspaces.devices.accessTicket.modelOptions.none",
                  { defaultValue: "none" },
                )}
              />
              <FormControlLabel
                value="zebra_zd411"
                control={<Radio color="primary" />}
                label={t(
                  "dsokCustomerDetail.workspaces.devices.accessTicket.modelOptions.zebrazd411",
                  { defaultValue: "zebra_zd411" },
                )}
              />
            </RadioGroup>
            {accessTicketModelTouched && accessTicketModelError ? (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {accessTicketModelError}
              </Typography>
            ) : null}
          </FormControl>
        )}

        {type === "turnstile" && (
          <>
            <FormControl
              component="fieldset"
              variant="standard"
              sx={{ mt: 1 }}
              error={Boolean(turnstileTypeTouched && turnstileTypeError)}
            >
              <FormLabel component="legend">
                {t("dsokCustomerDetail.workspaces.devices.turnstile.type", {
                  defaultValue: "Turnstile type",
                })}
              </FormLabel>
              <RadioGroup
                name={`turnstile-type-${String(resolvedDeviceUuid)}`}
                value={additionalData?.turnstileType ?? "turnstile"}
                onChange={(e) => {
                  const nextTurnstileType = e.target.value;
                  setFieldValue(
                    `${baseDevicePath}.additionalData`,
                    {
                      turnstileType: nextTurnstileType,
                      qrCodeIdIn: additionalData?.qrCodeIdIn ?? "",
                      qrCodeIdOut: additionalData?.qrCodeIdOut ?? "",
                      qrCodeIpIn: additionalData?.qrCodeIpIn ?? "",
                      qrCodeIpOut: additionalData?.qrCodeIpOut ?? "",
                      openRelayNumberIn: defaultTurnstileOpenRelayNumber(
                        additionalData?.openRelayNumberIn,
                      ),
                      openRelayNumberOut: defaultTurnstileOpenRelayNumber(
                        additionalData?.openRelayNumberOut,
                      ),
                      disableRelayNumberIn: defaultTurnstileOpenRelayNumber(
                        additionalData?.disableRelayNumberIn,
                      ),
                      disableRelayNumberOut: defaultTurnstileOpenRelayNumber(
                        additionalData?.disableRelayNumberOut,
                      ),
                    },
                    false,
                  );
                }}
              >
                <FormControlLabel
                  value="turnstile"
                  control={<Radio color="primary" />}
                  label={t(
                    "dsokCustomerDetail.workspaces.devices.turnstile.typeOptions.turnstile",
                    { defaultValue: "turnstile" },
                  )}
                />
                <FormControlLabel
                  value="gate"
                  control={<Radio color="primary" />}
                  label={t(
                    "dsokCustomerDetail.workspaces.devices.turnstile.typeOptions.gate",
                    { defaultValue: "gate" },
                  )}
                />
              </RadioGroup>
              {turnstileTypeTouched && turnstileTypeError ? (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {turnstileTypeError}
                </Typography>
              ) : null}
            </FormControl>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ rowGap: 2, mt: 1 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <JumboTextField
                  fullWidth
                  required
                  name={`${baseDevicePath}.additionalData.qrCodeIdIn`}
                  label={t(
                    "dsokCustomerDetail.workspaces.devices.turnstile.qrCodeIdIn",
                    {
                      defaultValue: "ID QR code IN",
                    },
                  )}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <JumboTextField
                  fullWidth
                  name={`${baseDevicePath}.additionalData.qrCodeIpIn`}
                  label={t(
                    "dsokCustomerDetail.workspaces.devices.turnstile.qrCodeIpIn",
                    {
                      defaultValue: "IP Adresa IN",
                    },
                  )}
                />
              </Box>
              <Button
                type="button"
                variant="outlined"
                onClick={() => handleOpenTurnstileQrModal("in")}
                disabled={qrGenerationInDisabled}
                sx={{
                  height: 40,
                  flexShrink: 0,
                  alignSelf: { xs: "stretch", sm: "center" },
                }}
              >
                {turnstileQrLoading ? (
                  <CircularProgress size={18} />
                ) : (
                  t(
                    "dsokCustomerDetail.workspaces.devices.turnstile.qrButton",
                    {
                      defaultValue: "QR kód",
                    },
                  )
                )}
              </Button>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <JumboTextField
                  fullWidth
                  required
                  name={`${baseDevicePath}.additionalData.qrCodeIdOut`}
                  label={t(
                    "dsokCustomerDetail.workspaces.devices.turnstile.qrCodeIdOut",
                    {
                      defaultValue: "ID QR code OUT",
                    },
                  )}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <JumboTextField
                  fullWidth
                  name={`${baseDevicePath}.additionalData.qrCodeIpOut`}
                  label={t(
                    "dsokCustomerDetail.workspaces.devices.turnstile.qrCodeIpOut",
                    {
                      defaultValue: "IP Adresa OUT",
                    },
                  )}
                />
              </Box>
              <Button
                type="button"
                variant="outlined"
                onClick={() => handleOpenTurnstileQrModal("out")}
                disabled={qrGenerationOutDisabled}
                sx={{
                  height: 40,
                  flexShrink: 0,
                  alignSelf: { xs: "stretch", sm: "center" },
                }}
              >
                {turnstileQrLoading ? (
                  <CircularProgress size={18} />
                ) : (
                  t(
                    "dsokCustomerDetail.workspaces.devices.turnstile.qrButton",
                    {
                      defaultValue: "QR kód",
                    },
                  )
                )}
              </Button>
            </Stack>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ rowGap: 2 }}
            >
              <Box sx={{ flex: 0.5, minWidth: 0 }}>
                <FormControl
                  fullWidth
                  required
                  size="small"
                  error={Boolean(openRelayInTouched && openRelayInError)}
                >
                  <InputLabel
                    id={`turnstile-open-relay-in-${String(resolvedDeviceUuid)}`}
                  >
                    {t(
                      "dsokCustomerDetail.workspaces.devices.turnstile.openRelayNumberIn",
                      {
                        defaultValue: "Open Relay Number IN",
                      },
                    )}
                  </InputLabel>
                  <Select
                    labelId={`turnstile-open-relay-in-${String(resolvedDeviceUuid)}`}
                    id={`turnstile-open-relay-in-select-${String(resolvedDeviceUuid)}`}
                    label={t(
                      "dsokCustomerDetail.workspaces.devices.turnstile.openRelayNumberIn",
                      {
                        defaultValue: "Open Relay Number IN",
                      },
                    )}
                    value={defaultTurnstileOpenRelayNumber(
                      additionalData?.openRelayNumberIn,
                    )}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setFieldValue(
                        `${baseDevicePath}.additionalData.openRelayNumberIn`,
                        v,
                      );
                    }}
                  >
                    {TURNSTILE_OPEN_RELAY_VALUES.map((n) => (
                      <MenuItem key={n} value={n}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>
                  {openRelayInTouched && openRelayInError ? (
                    <FormHelperText>{openRelayInError}</FormHelperText>
                  ) : null}
                </FormControl>
              </Box>
              <Box sx={{ flex: 0.5, minWidth: 0 }}>
                <FormControl
                  fullWidth
                  required
                  size="small"
                  error={Boolean(disableRelayInTouched && disableRelayInError)}
                >
                  <InputLabel
                    id={`turnstile-disable-relay-in-${String(resolvedDeviceUuid)}`}
                  >
                    {t(
                      "dsokCustomerDetail.workspaces.devices.turnstile.disableRelayNumberIn",
                      {
                        defaultValue: "Disable Relay Number IN",
                      },
                    )}
                  </InputLabel>
                  <Select
                    labelId={`turnstile-disable-relay-in-${String(resolvedDeviceUuid)}`}
                    id={`turnstile-disable-relay-in-select-${String(resolvedDeviceUuid)}`}
                    label={t(
                      "dsokCustomerDetail.workspaces.devices.turnstile.disableRelayNumberIn",
                      {
                        defaultValue: "Disable Relay Number IN",
                      },
                    )}
                    value={defaultTurnstileOpenRelayNumber(
                      additionalData?.disableRelayNumberIn,
                    )}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setFieldValue(
                        `${baseDevicePath}.additionalData.disableRelayNumberIn`,
                        v,
                      );
                    }}
                  >
                    {TURNSTILE_OPEN_RELAY_VALUES.map((n) => (
                      <MenuItem key={n} value={n}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>
                  {disableRelayInTouched && disableRelayInError ? (
                    <FormHelperText>{disableRelayInError}</FormHelperText>
                  ) : null}
                </FormControl>
              </Box>
              <Button
                type="button"
                variant="contained"
                onClick={() => handleOpenTurnstile("in")}
                disabled={turnstileOpenLoading != null}
                sx={{
                  height: 40,
                  flexShrink: 0,
                  alignSelf: { xs: "stretch", sm: "center" },
                }}
              >
                {turnstileOpenLoading === "in" ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  t(
                    "dsokCustomerDetail.workspaces.devices.turnstile.openAction.in",
                    {
                      defaultValue: "Otvoriť IN",
                    },
                  )
                )}
              </Button>
              <Box sx={{ flex: 0.5, minWidth: 0 }}>
                <FormControl
                  fullWidth
                  required
                  size="small"
                  error={Boolean(openRelayOutTouched && openRelayOutError)}
                >
                  <InputLabel
                    id={`turnstile-open-relay-out-${String(resolvedDeviceUuid)}`}
                  >
                    {t(
                      "dsokCustomerDetail.workspaces.devices.turnstile.openRelayNumberOut",
                      {
                        defaultValue: "Open Relay Number OUT",
                      },
                    )}
                  </InputLabel>
                  <Select
                    labelId={`turnstile-open-relay-out-${String(resolvedDeviceUuid)}`}
                    id={`turnstile-open-relay-out-select-${String(resolvedDeviceUuid)}`}
                    label={t(
                      "dsokCustomerDetail.workspaces.devices.turnstile.openRelayNumberOut",
                      {
                        defaultValue: "Open Relay Number OUT",
                      },
                    )}
                    value={defaultTurnstileOpenRelayNumber(
                      additionalData?.openRelayNumberOut,
                    )}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setFieldValue(
                        `${baseDevicePath}.additionalData.openRelayNumberOut`,
                        v,
                      );
                    }}
                  >
                    {TURNSTILE_OPEN_RELAY_VALUES.map((n) => (
                      <MenuItem key={n} value={n}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>
                  {openRelayOutTouched && openRelayOutError ? (
                    <FormHelperText>{openRelayOutError}</FormHelperText>
                  ) : null}
                </FormControl>
              </Box>
              <Box sx={{ flex: 0.5, minWidth: 0 }}>
                <FormControl
                  fullWidth
                  required
                  size="small"
                  error={Boolean(
                    disableRelayOutTouched && disableRelayOutError,
                  )}
                >
                  <InputLabel
                    id={`turnstile-disable-relay-out-${String(resolvedDeviceUuid)}`}
                  >
                    {t(
                      "dsokCustomerDetail.workspaces.devices.turnstile.disableRelayNumberOut",
                      {
                        defaultValue: "Disable Relay Number OUT",
                      },
                    )}
                  </InputLabel>
                  <Select
                    labelId={`turnstile-disable-relay-out-${String(resolvedDeviceUuid)}`}
                    id={`turnstile-disable-relay-out-select-${String(resolvedDeviceUuid)}`}
                    label={t(
                      "dsokCustomerDetail.workspaces.devices.turnstile.disableRelayNumberOut",
                      {
                        defaultValue: "Disable Relay Number OUT",
                      },
                    )}
                    value={defaultTurnstileOpenRelayNumber(
                      additionalData?.disableRelayNumberOut,
                    )}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setFieldValue(
                        `${baseDevicePath}.additionalData.disableRelayNumberOut`,
                        v,
                      );
                    }}
                  >
                    {TURNSTILE_OPEN_RELAY_VALUES.map((n) => (
                      <MenuItem key={n} value={n}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>
                  {disableRelayOutTouched && disableRelayOutError ? (
                    <FormHelperText>{disableRelayOutError}</FormHelperText>
                  ) : null}
                </FormControl>
              </Box>
              <Button
                type="button"
                variant="contained"
                onClick={() => handleOpenTurnstile("out")}
                disabled={turnstileOpenLoading != null}
                sx={{
                  height: 40,
                  flexShrink: 0,
                  alignSelf: { xs: "stretch", sm: "center" },
                }}
              >
                {turnstileOpenLoading === "out" ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  t(
                    "dsokCustomerDetail.workspaces.devices.turnstile.openAction.out",
                    {
                      defaultValue: "Otvoriť OUT",
                    },
                  )
                )}
              </Button>
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t(
                "dsokCustomerDetail.workspaces.devices.turnstile.configurationHelp",
                {
                  defaultValue:
                    "ID QR code IN and ID QR code OUT must be configured in the QR reader. Open and disable relay numbers for IN and OUT are configured directly on the turnstile.",
                },
              )}
            </Typography>
            <TurnstileQrCodeModal
              open={turnstileQrModalOpen}
              onClose={() => setTurnstileQrModalOpen(false)}
              qrConfig={turnstileQrConfig}
              qrLabel={turnstileQrLabel}
              t={t}
            />
          </>
        )}

        {type === "compressor" && (
          <>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ rowGap: 2, mt: 1 }}
              alignItems={{ xs: "stretch", sm: "flex-end" }}
            >
              <Box sx={{ flex: 0.5, minWidth: 0 }}>
                <FormControl fullWidth required size="small">
                  <InputLabel
                    id={`compressor-start-relay-${String(resolvedDeviceUuid)}`}
                  >
                    {t(
                      "dsokCustomerDetail.workspaces.devices.compressor.startRelayNumber",
                      { defaultValue: "Start relay number" },
                    )}
                  </InputLabel>
                  <Select
                    labelId={`compressor-start-relay-${String(resolvedDeviceUuid)}`}
                    id={`compressor-start-relay-select-${String(resolvedDeviceUuid)}`}
                    label={t(
                      "dsokCustomerDetail.workspaces.devices.compressor.startRelayNumber",
                      { defaultValue: "Start relay number" },
                    )}
                    value={defaultTurnstileOpenRelayNumber(
                      additionalData?.startRelayNumber,
                    )}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setFieldValue(
                        `${baseDevicePath}.additionalData.startRelayNumber`,
                        v,
                      );
                    }}
                  >
                    {COMPRESSOR_RELAY_VALUES.map((n) => (
                      <MenuItem key={n} value={n}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: 0.5, minWidth: 0 }}>
                <FormControl fullWidth required size="small">
                  <InputLabel
                    id={`compressor-disable-relay-${String(resolvedDeviceUuid)}`}
                  >
                    {t(
                      "dsokCustomerDetail.workspaces.devices.compressor.disableRelayNumber",
                      { defaultValue: "Disable relay number" },
                    )}
                  </InputLabel>
                  <Select
                    labelId={`compressor-disable-relay-${String(resolvedDeviceUuid)}`}
                    id={`compressor-disable-relay-select-${String(resolvedDeviceUuid)}`}
                    label={t(
                      "dsokCustomerDetail.workspaces.devices.compressor.disableRelayNumber",
                      { defaultValue: "Disable relay number" },
                    )}
                    value={defaultTurnstileOpenRelayNumber(
                      additionalData?.disableRelayNumber,
                    )}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setFieldValue(
                        `${baseDevicePath}.additionalData.disableRelayNumber`,
                        v,
                      );
                    }}
                  >
                    {COMPRESSOR_RELAY_VALUES.map((n) => (
                      <MenuItem key={n} value={n}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Button
                type="button"
                variant="contained"
                onClick={handleStartCompressor}
                disabled={compressorStartLoading}
                sx={{
                  height: 40,
                  flexShrink: 0,
                  alignSelf: { xs: "stretch", sm: "center" },
                }}
              >
                {compressorStartLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  t(
                    "dsokCustomerDetail.workspaces.devices.compressor.startAction.button",
                    { defaultValue: "Spustiť" },
                  )
                )}
              </Button>
              <Button
                type="button"
                variant="outlined"
                color="error"
                onClick={handleStopCompressor}
                disabled={compressorStopLoading}
                sx={{
                  height: 40,
                  flexShrink: 0,
                  alignSelf: { xs: "stretch", sm: "center" },
                }}
              >
                {compressorStopLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  t(
                    "dsokCustomerDetail.workspaces.devices.compressor.stopAction.button",
                    { defaultValue: "Vypnúť" },
                  )
                )}
              </Button>
            </Stack>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ rowGap: 1, mt: 1 }}
              alignItems={{ xs: "stretch", sm: "center" }}
            >
              <Tooltip
                arrow
                title={t(
                  "dsokCustomerDetail.workspaces.devices.compressor.autoOffEnabledHelp",
                  {
                    defaultValue:
                      "Ak je aktívne, DLA po nastavenom počte hodín prevádzky sám vypne kompresor (pulz na Disable relé). Časovač sa obnoví pri každom spustení a zruší pri ručnom vypnutí.",
                  },
                )}
              >
                <Box component="span">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={additionalData?.autoOffEnabled === true}
                        onChange={(e) => {
                          setFieldValue(
                            `${baseDevicePath}.additionalData.autoOffEnabled`,
                            e.target.checked,
                          );
                          if (
                            e.target.checked &&
                            !Number.isInteger(
                              Number(additionalData?.autoOffHours),
                            )
                          ) {
                            setFieldValue(
                              `${baseDevicePath}.additionalData.autoOffHours`,
                              normalizeCompressorAutoOffHours(
                                additionalData?.autoOffHours,
                              ),
                            );
                          }
                        }}
                      />
                    }
                    label={t(
                      "dsokCustomerDetail.workspaces.devices.compressor.autoOffEnabled",
                      { defaultValue: "Automatické vypnutie" },
                    )}
                  />
                </Box>
              </Tooltip>
              {additionalData?.autoOffEnabled === true && (
                <Box sx={{ flex: 0.5, minWidth: 0 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel
                      id={`compressor-auto-off-hours-${String(resolvedDeviceUuid)}`}
                    >
                      {t(
                        "dsokCustomerDetail.workspaces.devices.compressor.autoOffHours",
                        { defaultValue: "Vypnúť po (hodín)" },
                      )}
                    </InputLabel>
                    <Select
                      labelId={`compressor-auto-off-hours-${String(resolvedDeviceUuid)}`}
                      id={`compressor-auto-off-hours-select-${String(resolvedDeviceUuid)}`}
                      label={t(
                        "dsokCustomerDetail.workspaces.devices.compressor.autoOffHours",
                        { defaultValue: "Vypnúť po (hodín)" },
                      )}
                      value={normalizeCompressorAutoOffHours(
                        additionalData?.autoOffHours,
                      )}
                      onChange={(e) => {
                        setFieldValue(
                          `${baseDevicePath}.additionalData.autoOffHours`,
                          normalizeCompressorAutoOffHours(e.target.value),
                        );
                      }}
                    >
                      {COMPRESSOR_AUTO_OFF_HOURS_VALUES.map((n) => (
                        <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t(
                "dsokCustomerDetail.workspaces.devices.compressor.configurationHelp",
                {
                  defaultValue:
                    "Ovládanie kompresora prebieha cez DLA jednorazovým pulzom relé (rovnako ako otvorenie turniketu). „Spustiť“ pulzne Start relé, „Vypnúť“ pulzne Disable relé. IP adresa zariadenia musí byť správne vyplnená vyššie.",
                },
              )}
            </Typography>
          </>
        )}
      </Stack>
    </Paper>
  );
}
